<script>
import {computed, defineComponent, onMounted, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useLanguages} from "@/hooks/useLanguages";
import {useCountries} from "@/hooks/countries";
import {useRoles} from "@/hooks/role";
import {useUsers} from "@/hooks/user";
import {useI18n} from "vue-i18n";
import {useMeta} from "vue-meta";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import MultiSelect from "primevue/multiselect";
import Breadcrumb from "@/components/Breadcrumb.vue";
import MainCard from "@/components/cards/MainCard.vue";
import ButtonSuccess from "@/components/buttons/ButtonSuccess.vue";
import InputNumberPhone from "@/components/inputs/InputNumberPhone.vue";
import SelectPhoneModal from "@/components/modals/SelectPhoneModal.vue";
import ProgressSpinner from 'primevue/progressspinner';
import {useStore} from "vuex";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import Paginator from "primevue/paginator";

export default defineComponent({
  layout: {
    name: 'AdminLayout',
  },
  components: {
    ConfirmationModal,
    Column, DataTable,
    Breadcrumb, MainCard, InputText,
    Dropdown, Button, MultiSelect,
    ButtonSuccess, InputNumberPhone, SelectPhoneModal, ProgressSpinner,
    Paginator,
  },
  setup() {
    const {t} = useI18n();
    const errors = ref(null)
    const store = useStore();

    useMeta({
      title: 'Редактирование пользователя'
    });

    const route = useRoute();
    const {loadRoles, roles} = useRoles();
    const router = useRouter();
    const {languages} = useLanguages();
    const {countries, selectCountry: country, loadCountries, toSelectCountryByPhoneCode} = useCountries();
    const {loadUser, updateUser, loadDocs, loadRooms, user, v$, form: formReactive, isUpdated} = useUsers();
    const rooms = computed(() => store.getters.getRoomsUser)
    const docs = computed(() => store.getters.getDocsUser)
    const deleteItem = ref(null)
    const isVisiblePopup = ref(false)
    const visible = ref(false);
    const isLoading = ref(true);
    const isSaveLoading = ref(false);
    const isDeleteLoading = ref(false);

    const oldRoles = () => {
      const oldRoles = user.value.roles.filter(item => item.name === 'chief' || item.name === 'customer' || item.name === 'owner')
      return oldRoles.length ? oldRoles.map(item => item.id) : [];
    }

    const saveUserData = async () => {
      errors.value = null
      isSaveLoading.value = true
      try {
        const data = {};
        for (let key in formReactive.value) {
          if (key === 'avatar') continue;
          data[key] = formReactive.value[key];
        }
        data.roles = [...data.roles, ...oldRoles()];
        await updateUser({
          id: route.params.id,
          data
        });
        await router.push('/users');
      } catch (e) {
        if (e.response.data.data) {
          errors.value = {...e.response.data.data}
        } else {
          errors.value = e.response.data.errors
        }
      }
      isSaveLoading.value = false
    };

    watch(() => formReactive.value, () => {
      isUpdated.value = false;
    }, {deep: true});

    const breadcrumbs = ref([]);

    const toggleChangePhoneCode = () => {
      visible.value = !visible.value
    }

    const changeVisibleModal = (data, type) => {
      isVisiblePopup.value = !isVisiblePopup.value;
      if (data) {
        deleteItem.value = {data, type}
      } else {
        deleteItem.value = null;
      }
    }

    const deleteHandler = async () => {
      isDeleteLoading.value = true
      if (deleteItem.value) {
        if(deleteItem.value.type === 'doc') {
          await store.dispatch('fetchDeleteDocUser', {
            id: user.value.id,
            document: deleteItem.value.data.id,
          });
        } else {
          await store.dispatch('fetchDeleteRoomUser', {
            id: user.value.id,
            room: deleteItem.value.data.id
          });
        }
      }
      isDeleteLoading.value = false
      changeVisibleModal(null)
    }

    const pageRooms = ref(0)
    const docsPage = ref(0)

    watch(() => pageRooms.value, async () => {
      await loadRooms(route.params.id, ((pageRooms.value / (rooms.value?.pagination?.per_page ?? 1)) + 1))
    })

    watch(() => docsPage.value, async () => {
      await loadDocs(route.params.id, ((docsPage.value / (docs.value?.pagination?.per_page ?? 1)) + 1))
    })

    onMounted(async () => {
      try {
        await loadUser(route.params.id);
        await loadRoles();
        await loadCountries();
        toSelectCountryByPhoneCode();
        const label = ((user.value.last_name ? user.value.last_name + ' ' : '') ?? ' -')
            + ((user.value.first_name ? user.value.first_name + ' ' : '') ?? ' -')
            + ((user.value.patronymic ? user.value.patronymic + ' ' : '') ?? ' -');

        breadcrumbs.value = [
          {label: t('pages-names.edit-user'), router: {name: 'users'}},
          {label}
        ];
        formReactive.value = {...user.value};
        formReactive.value.roles = user.value.roles.filter(item => item.name !== 'chief' && item.name !== 'customer' && item.name !== 'owner').map(role => role.id);
      } catch (e) {
        console.error(e)
      }
      isLoading.value = false
    });

    return {
      user,
      roles,
      breadcrumbs,
      formReactive,
      saveUserData,
      isUpdated,
      visible,
      languages,
      countries,
      country,
      t,
      v$,
      errors,
      toggleChangePhoneCode,
      isLoading,
      isSaveLoading,
      rooms,
      docs,
      changeVisibleModal,
      isDeleteLoading,
      deleteHandler,
      deleteItem,
      isVisiblePopup,
      pageRooms,
      docsPage,
    };
  }
})
</script>

<template>
  <SelectPhoneModal
      v-model:modelValue="visible"
      :countries="countries"
  />
  <ConfirmationModal :visible="isVisiblePopup">
    <template #header>
      {{ deleteItem.type ==='doc' ? t('user.docs.popup.title') : t('user.rooms.popup.title') }}
    </template>

    <template #default>
      <span>{{ deleteItem.type ==='doc' ? t('user.docs.popup.description') : t('user.rooms.popup.description') }}</span>
    </template>

    <template #footer>
      <div class="flex justify-content-between mt-4">
        <Button :label="t('labels.cancel')" @click="changeVisibleModal(null)" class="btn-primary-outlined font-light w-12"/>
        <Button :label="t('labels.destroy')" :loading="isDeleteLoading" @click="deleteHandler" class="btn-primary font-light ml-3 w-12"/>
      </div>
    </template>
  </ConfirmationModal>
  <div v-if="isLoading" class="spinner__wrapper">
    <ProgressSpinner />
  </div>
  <section v-else class="py-2 mb-3">
    <div class="flex justify-content-between mb-3">
      <Breadcrumb :data="breadcrumbs" separator="/"/>

      <div class="flex">
        <Button v-if="!isUpdated" :loading="isSaveLoading" :label="t('labels.save-changes')" class="btn-primary font-light" @click="saveUserData"/>
        <ButtonSuccess v-if="isUpdated" :label="t('labels.changes-saved')"/>
      </div>
    </div>
    <div class="flex justify-content-between mb-3">
      <span class="color-black-40">
        <template v-for="(role, i) in user?.roles" :key="i">
          {{ role.name_ru }}
          <template v-if="i !== user.roles.length - 1"> + </template>
        </template>
      </span>
    </div>

    <div class="grid mb-3 h-max">
      <div class="col-12 md:col-4">
        <MainCard :styles="{'h-full': true}" :title="t('card-names.basic-registration-information')">
          <div class="flex flex-column gap-3">
            <div class="mb-3">
              <span class="p-float-label w-full">
                <InputText
                    v-model="formReactive.last_name"
                    :class="['w-full', {'p-invalid': v$.last_name.$errors.length || errors?.last_name}]"
                    id="last_name"
                />
                <label for="last_name">{{ t('labels.last-name') }}<i style="color: #E26B6B">*</i></label>
              </span>
              <span v-if="v$.last_name.$errors.length || errors?.last_name" class="color-red text-xs">
                {{ errors ? errors.last_name[0] : t(v$.last_name.$errors[0].$message) }}
              </span>
            </div>
            <div class="mb-3">
              <span class="p-float-label w-full">
                <InputText
                    id="first_name"
                    :class="['w-full', {'p-invalid': v$.first_name.$errors.length || errors?.first_name}]"
                    v-model="formReactive.first_name"
                />
                <label for="first_name">{{ t('labels.first-name') }}<i style="color: #E26B6B">*</i></label>
              </span>
              <span v-if="v$.first_name.$errors.length || errors?.first_name" class="color-red text-xs">
                {{ errors ? errors.first_name[0] : t(v$.first_name.$errors[0].$message) }}
              </span>
            </div>
            <div class="mb-3">
              <span class="p-float-label w-full">
                <InputText
                    id="patronymic"
                    :class="['w-full', {'p-invalid': errors?.patronymic}]"
                    v-model="formReactive.patronymic"
                />
                <label for="patronymic">{{ t('labels.patronymic') }}</label>
              </span>
              <span v-if="errors?.patronymic" class="color-red text-xs">
                {{ errors.patronymic[0] }}
              </span>
            </div>
          </div>
        </MainCard>
      </div>
      <div class="col-12 md:col-4">
        <MainCard :styles="{'h-full': true}" :title="t('card-names.contact-data')">
          <div class="flex flex-column gap-3">
            <div class="mb-3">
              <InputNumberPhone
                  v-model="formReactive.phone"
                  :class="{'p-invalid': v$.phone.$errors.length || errors?.phone}"
                  @toggleChangePhoneCode="toggleChangePhoneCode"
                  :phone-code="country?.phone_code ? country.phone_code : '+7'"
                  :country="country?.name"
                  isRequired
              />
              <span v-if="v$.phone.$errors.length || errors?.phone" class="color-red text-xs">
                {{ errors ? errors.phone[0] : t(v$.phone.$errors[0].$message) }}
              </span>
            </div>

            <div class="mb-3">
              <span class="p-float-label w-full">
                <InputText
                    id="email"
                    :class="['w-full', {'p-invalid': v$.email.$errors.length || errors?.email}]"
                    v-model="formReactive.email"
                />
                <label for="email">{{ t('labels.email-for-notices') }}</label>
              </span>
              <span v-if="v$.email.$errors.length || errors?.email" class="color-red text-xs">
                {{ errors ? errors.email[0] : t(v$.email.$errors[0].$message) }}
              </span>
            </div>
          </div>
        </MainCard>
      </div>
      <div class="col-12 md:col-4">
        <div class="flex flex-column gap-3">
          <MainCard :title="t('card-names.role')">
            <MultiSelect
                v-model="formReactive.roles"
                :class="{'p-invalid': errors?.roles}"
                display="chip"
                :options="roles"
                optionLabel="name_ru"
                option-value="id"
                :placeholder="t('placeholders.roles')"
                class="w-full"
            />

            <span v-if="errors?.roles" class="color-error text-xs">
              {{ errors.roles[0] }}
            </span>
          </MainCard>

<!--          <MainCard :title="t('card-names.language')" isRequired>-->
<!--            <Dropdown-->
<!--                v-model="formReactive.language"-->
<!--                :class="{'p-invalid': v$.language?.$errors?.length || errors?.language}"-->
<!--                :options="languages"-->
<!--                optionLabel="label"-->
<!--                option-value="value"-->
<!--                :placeholder="t('card-names.language')"-->
<!--                class="w-full"-->
<!--            />-->

<!--            <span v-if="v$.language?.$errors?.length || errors?.language" class="color-error text-xs">-->
<!--              {{ errors ? errors.language[0] : t(v$.language.$errors[0].$message) }}-->
<!--            </span>-->
<!--          </MainCard>-->
<!--          <MainCard title="Быстрые ссылки"></MainCard>-->
        </div>
      </div>
    </div>
    <div class="user__wrapper">
      <span class="user__title">{{t('user.rooms.title')}}</span>
      <DataTable :value="rooms?.data?.length ? rooms.data : []" :loading="isLoading" showGridlines tableStyle="min-width: 50rem" selectionMode="single">
        <Column field="pib" :header="t('user.rooms.pib')">
          <template #body="slotProps">
            <span>{{ slotProps.data.pib }}</span>
          </template>
        </Column>
        <Column field="state" :header="t('user.rooms.status')">
          <template #body="slotProps">
            <span :class="slotProps.data.state">{{ slotProps.data.state === 'uncontrolled' ? t('user.rooms.uncontrolled') : t('user.rooms.controlled') }}</span>
          </template>
        </Column>
        <Column field="action" header="">
          <template #body="slotProps">
            <button v-if="!slotProps.data.deleted" class="user__table-button" @click="changeVisibleModal(slotProps.data, 'room')">
              <img src="@/assets/images/icons/Delete.svg" alt="delete">
            </button>
          </template>
        </Column>
      </DataTable>
      <Paginator
          v-if="rooms?.pagination.total > rooms?.pagination?.per_page"
          v-model:first="pageRooms"
          :rows="rooms?.pagination?.per_page ?? 0"
          :totalRecords="rooms?.pagination?.total ?? 0"
          class="justify-content-start"
      ></Paginator>
    </div>
    <div class="user__wrapper">
      <span class="user__title">{{t('user.docs.title')}}</span>
      <DataTable :value="docs?.data?.length ? docs.data : []" :loading="isLoading" showGridlines tableStyle="min-width: 50rem" selectionMode="single">
        <Column field="pib" :header="t('user.docs.name')">
          <template #body="slotProps">
            <div class="user__docs-name">
              <img src="@/assets/images/icons/file-text.svg" alt="document" />
              {{ slotProps.data.title }}
            </div>
          </template>
        </Column>
        <Column field="state" :header="t('user.docs.status')">
          <template #body="slotProps">
            <span v-if="slotProps.data.status" class="user__docs-status">{{ slotProps.data.status }}</span>
            <span v-else class="">—</span>
          </template>
        </Column>
        <Column field="action" header="">
          <template #body="slotProps">
            <button class="user__table-button" @click="changeVisibleModal(slotProps.data, 'doc')">
              <img src="@/assets/images/icons/Delete.svg" alt="delete">
            </button>
          </template>
        </Column>
      </DataTable>
      <Paginator
          v-if="docs?.pagination.total > docs?.pagination?.per_page"
          v-model:first="docsPage"
          :rows="docs?.pagination?.per_page ?? 0"
          :totalRecords="docs?.pagination?.total ?? 0"
          class="justify-content-start"
      ></Paginator>
    </div>
    <div>
    </div>
  </section>

</template>

<style lang="scss" scoped>
.spinner__wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 500px;
}
::v-deep(.p-progress-spinner-circle) {
  stroke: var(--color-primary) !important;
}
.user__wrapper {
  display: flex;
  flex-direction: column;
  margin-top: 30px;
  gap: 20px;
}
.user__title {
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  color: #333333;
}

.user__table-button {
  background-color: unset;
  border: unset;
  cursor: pointer;
  width: 100px;
}
:deep(.p-datatable-thead) {
  th:first-child {
    width: 65%;
    .p-column-header-content {
      text-align: start !important;
      justify-content: start !important;
    }
  }
}
:deep(.p-selectable-row) {
  td:first-child {
    text-align: start !important;
    width: 70%;
    justify-content: start !important;
  }
  td:nth-child(2) {
    width: 30%;
    min-width: 300px;
  }
}
.uncontrolled {
  font-weight: 600;
  font-size: 13px;
  line-height: 22px;
  text-align: center;
  color: #333333;
  padding: 5px 12px;
  background-color: #E1E8ED;
  border-radius: 30px;
}
.controlled {
  font-weight: 600;
  font-size: 13px;
  line-height: 22px;
  text-align: center;
  color: #FFFFFF;
  padding: 5px 12px;
  background-color: var(--color-primary);
  border-radius: 30px;
}

.user__docs-name {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 16px;
  line-height: 18px;
  color: #C3987A;
  word-break: break-all;
}
.user__docs-status {
  font-weight: 600;
  font-size: 13px;
  line-height: 22px;
  text-align: center;
  color: #333333;
  padding: 5px 12px;
  background-color: #E1E8ED;
  border-radius: 30px;
}

</style>
